import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { UpsertEmailTemplateDto } from '../../../../EmailTemplates/SetupEmailTemplatePage/UpsertEmailTemplateDto';
import {
  EmailTemplateEditor,
  emailTemplateToDto,
  MAX_FILES_SIZE_MB,
} from '../../../../../components/EmailTemplateEditor';
import { FileContent, useImperativeFilePicker } from 'use-file-picker';
import { FilesLimitValidator } from '../../../../../utils/useFilePicker/filesLimitValidator';
import { FilesSizeValidator } from '../../../../../utils/useFilePicker/filesSizeValidator';
import { MAX_FILE_SIZE_MB, MAX_FILES } from '../../../../SendEmailModal';
import { CalendarEventSelector } from '../InterviewDetails/CalendarEventSelector';
import { EmailTemplate } from '../../../../../entities/EmailTemplate';
import { useEmailTemplate } from '../../../../../queries/useEmailTemplate';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Button } from 'reactstrap';
import slyles from '../styles.module.scss';
import { useFeatureFlags } from '../../../../../contexts/FeatureFlagContext';

interface PropTypes {
  referenceTemplate: EmailTemplate;
  calendarEventTemplateDto?: UpsertEmailTemplateDto;
  onCancel: () => void;
  onSubmit: (data: UpsertEmailTemplateDto) => void;
  setReferenceTemplate: (template: EmailTemplate) => void;
}

function BodyFooter() {
  const featureflags = useFeatureFlags();

  return (
    <div className='fs-6 text-dark-200'>
      Changes will not override the template.{' '}
      {featureflags.personalizingSchedulingInvitesV2FfEnabled && (
        <>
          Admins can edit by going to{' '}
          <a
            href='/organization_settings/email_templates'
            className='text-info'
          >
            Settings &gt; Email Templates
          </a>
        </>
      )}
    </div>
  );
}

export function CalendarEventPanel(props: PropTypes) {
  const { data: referenceTemplateWithAttachments, isLoading } =
    useEmailTemplate(props.referenceTemplate?.id);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm<UpsertEmailTemplateDto>({
    mode: 'onChange',
    defaultValues:
      props.calendarEventTemplateDto ??
      (!isLoading && emailTemplateToDto(referenceTemplateWithAttachments)),
  });

  const { fields, append, remove } = useFieldArray({
    name: 'attachments',
    control: control,
  });

  const handleRemoveField = (index: number) => {
    const toDelete = fields[index];
    const plainFileToDelete = plainFiles.find(
      (i) => i.name === toDelete.filename,
    );

    remove(index);

    if (plainFileToDelete != null) {
      removeFileByReference(plainFileToDelete);
    }
  };

  const [
    openFileSelector,
    {
      filesContent,
      errors: filePickerErrors,
      plainFiles,
      removeFileByReference,
    },
  ] = useImperativeFilePicker({
    multiple: true,
    readAs: 'DataURL',
    maxFileSize: MAX_FILE_SIZE_MB,
    readFilesContent: true,
    limitFilesConfig: {
      max: MAX_FILES,
    },
    validators: [
      new FilesSizeValidator(
        fields.map((v) => v.size),
        MAX_FILES_SIZE_MB,
      ),
      new FilesLimitValidator(fields.length, MAX_FILES),
    ],
  });

  const onSubmit = handleSubmit((data: UpsertEmailTemplateDto) => {
    props.onSubmit({
      ...data,
      emailTemplateReferenceId: referenceTemplateWithAttachments?.id,
      attachments: fields.map((field) => ({
        id: field.persistedId,
        filename: field.filename,
        contentBase64: field.contentBase64,
        size: field.size,
      })),
    });
  });

  useEffect(() => {
    if (referenceTemplateWithAttachments != null) {
      setValue('emailTemplateReferenceId', referenceTemplateWithAttachments.id);
      setValue(
        'attachments',
        referenceTemplateWithAttachments.attachments.map((attachment) => ({
          persistedId: attachment.id, // Use backend-provided id
          filename: attachment.filename,
          contentBase64: attachment.contentBase64,
          size: attachment.size,
        })),
      );
    }
  }, [referenceTemplateWithAttachments]);

  useEffect(() => {
    filesContent.forEach((fileContent: FileContent, index: number) => {
      if (fields.find((i) => i.filename === fileContent.name) == null) {
        append({
          filename: fileContent.name,
          contentBase64: fileContent.content,
          size: plainFiles[index].size,
        });
      }
    });
  }, [filesContent, referenceTemplateWithAttachments]);

  if (isLoading) return <LoadingSpinner />;

  return (
    <form onSubmit={onSubmit}>
      <h3 className='mb-4'>Edit Calendar Event</h3>
      <CalendarEventSelector
        title='Template'
        size='md'
        labelClassName='fw-semibold'
        calendarEventTemplate={props.referenceTemplate}
        emailTemplateId={props.referenceTemplate?.id}
        setCalendarEventTemplate={props.setReferenceTemplate}
      />
      <EmailTemplateEditor
        skipAttachments={true} // Skip attachments until we implement those methods on cronofy gem
        control={control}
        errors={errors}
        footerFields={fields}
        filePickerErrors={filePickerErrors}
        referenceTemplate={
          props.calendarEventTemplateDto ?? referenceTemplateWithAttachments
        }
        bodySize='xs'
        title='Title'
        bodyTitle='Description'
        className={slyles['email-template-input']}
        subjectColSize={12}
        bodyColSize={12}
        bodyFooter={<BodyFooter />}
        handleRemoveField={handleRemoveField}
        openFileSelector={openFileSelector}
        setHtml={(html: string) => setValue('templateHtml', html)}
        setSubjectText={(subject: string) => setValue('subjectText', subject)}
      />
      <div className='mt-4 d-flex justify-content-start'>
        <Button color='primary' type='submit' className='me-3'>
          Save
        </Button>
        <Button color='danger' onClick={props.onCancel} type='button'>
          Cancel
        </Button>
      </div>
    </form>
  );
}
